import { action, makeObservable, observable } from 'mobx';
import { RootStore } from './root';
/* eslint-disable-next-line import/no-extraneous-dependencies -- resolved via tsconfig paths to sibling package source */
import {
  WebAuthnEnrollmentStore,
  CreatePasskeyErrorCode,
} from '@wix/webauthn-management';
import { shouldEnableStepUpOnPostLoginPasskeyEnroll } from './constants';
import { preMintStepUpAndEnroll } from './passkeyStepUp';

export type PasskeyEnrollmentStatus =
  | 'idle'
  | 'enrolling'
  | 'success'
  | 'error';

export type EnrollPasskeyResult =
  | { success: true; isBackedUp: boolean }
  | { success: false; cancelled: true }
  | {
      success: false;
      cancelled?: false;
      errorCode: CreatePasskeyErrorCode;
      errorMessage: string;
    };

export class PasskeyEnrollStore {
  errorCode: CreatePasskeyErrorCode | null = null;
  enrollmentStatus: PasskeyEnrollmentStatus = 'idle';
  private readonly webAuthnEnrollmentStore: WebAuthnEnrollmentStore;

  constructor(private readonly rootStore: RootStore) {
    this.webAuthnEnrollmentStore = new WebAuthnEnrollmentStore();
    makeObservable(this, {
      errorCode: observable,
      enrollmentStatus: observable,
      enrollPasskey: action.bound,
      reset: action.bound,
    });
  }

  async enrollPasskey(): Promise<EnrollPasskeyResult> {
    this.errorCode = null;
    this.enrollmentStatus = 'enrolling';
    // Abort any pending passkey login get() (e.g. conditional UI) before create().
    this.rootStore.webauthnStore.abortWebAuthnAuthentication();

    // FT off / unset keeps the enroll call exactly as it was before step-up.
    const result: EnrollPasskeyResult =
      shouldEnableStepUpOnPostLoginPasskeyEnroll(this.rootStore.experiments)
        ? await preMintStepUpAndEnroll({
            enrollmentStore: this.webAuthnEnrollmentStore,
            stepUpManager: this.rootStore.stepUpManager,
          })
        : await this.webAuthnEnrollmentStore.enrollWebAuthn();

    if (result.success) {
      this.enrollmentStatus = 'success';
      return result;
    }

    if (result.cancelled) {
      // Quiet abort: back to idle as if the click never happened — no error UI.
      this.enrollmentStatus = 'idle';
      this.errorCode = null;
      return result;
    }

    this.enrollmentStatus = 'error';
    this.errorCode = result.errorCode;
    return result;
  }

  reset(): void {
    this.enrollmentStatus = 'idle';
    this.errorCode = null;
  }
}
